export const LEGACY_ATTRIBUTION_STORAGE_KEY =
  "reputech_first_touch_attribution";
export const ATTRIBUTION_STORAGE_KEY =
  "reputech_first_touch_attribution_v2";
export const SESSION_STORAGE_KEY = "reputech_tracking_session";
export const ATTRIBUTION_TTL_MS = 90 * 24 * 60 * 60 * 1000;
// Uses localStorage, not sessionStorage: this is a fixed 24h app-session id.
export const SESSION_TTL_MS = 24 * 60 * 60 * 1000;
export const NURTURING_ATTRIBUTION_PARAM = "rh_nurturing";
export const NURTURING_ATTRIBUTION_STORAGE_KEY =
  "reputech_nurturing_attribution_v1";
export const NURTURING_ATTRIBUTION_TTL_MS =
  30 * 24 * 60 * 60 * 1000;
const NURTURING_TOKEN_PATTERN = /^[0-9a-f]{64}$/i;

export type StoredNurturingAttribution = {
  token: string;
  created_at: number;
  expires_at: number;
};

export const UTM_PARAM_NAMES = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
] as const;

export const GOOGLE_AD_PARAM_NAMES = [
  "gclid",
  "gbraid",
  "wbraid",
  "gad_source",
  "gad_campaignid",
  "gclsrc",
  "dclid",
] as const;

/**
 * Ad click ids that may stay inside stored/transmitted URLs under the same
 * consent rules as the Google click ids. `msclkid` (Microsoft Advertising) is
 * URL-only: the backend reads it from `landing_page`/`first_landing_page` for
 * Microsoft Ads offline conversion uploads, so it has no structured field.
 */
export const AD_CLICK_URL_PARAM_NAMES = [
  ...GOOGLE_AD_PARAM_NAMES,
  "msclkid",
] as const;

// Google Ads click ids are kept as current/last-known values; first-touch
// counterparts are intentionally limited to UTM fields.
export type UtmParamName = (typeof UTM_PARAM_NAMES)[number];
export type GoogleAdParamName = (typeof GOOGLE_AD_PARAM_NAMES)[number];
export type AttributionParamName = UtmParamName | GoogleAdParamName;

export type AttributionValues = Partial<Record<AttributionParamName, string>> & {
  first_landing_page?: string | null;
  first_referrer_url?: string | null;
  first_utm_source?: string | null;
  first_utm_medium?: string | null;
  first_utm_campaign?: string | null;
  first_utm_term?: string | null;
  first_utm_content?: string | null;
};

export type StoredAttribution = AttributionValues & {
  created_at: number;
  expires_at: number;
};

export type StoredSession = {
  session_id: string;
  created_at: number;
  expires_at: number;
};

type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;

// Page-lifetime fallback shared by TrackingService instances when first-party
// storage is unavailable or write-blocked.
let inMemorySession: StoredSession | null = null;
let inMemorySessionStorage: StorageLike | null = null;
let inMemoryAttribution: StoredAttribution | null = null;
let inMemoryNurturingAttribution: StoredNurturingAttribution | null = null;
// Page-lifetime migration guard. Tests reset this via
// resetInMemoryStateForTesting(); production intentionally does not.
let hasClearedLegacyAttributionStorage = false;

export const resetInMemoryStateForTesting = (): void => {
  inMemorySession = null;
  inMemorySessionStorage = null;
  inMemoryAttribution = null;
  inMemoryNurturingAttribution = null;
  hasClearedLegacyAttributionStorage = false;
};

export const resetInMemorySessionForTesting = resetInMemoryStateForTesting;

const safeJsonParse = <T>(value: string | null): T | null => {
  if (!value) return null;

  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
};

/**
 * Reads from the first underlying storage that returns a value and writes to
 * every storage that accepts the write. Used as the default first-party store
 * so browsers that block or partition localStorage keep working attribution for
 * at least the current tab session through the sessionStorage mirror.
 */
class CompositeStorage implements StorageLike {
  private readonly storages: StorageLike[];

  constructor(storages: StorageLike[]) {
    this.storages = storages;
  }

  getItem(key: string): string | null {
    for (const storage of this.storages) {
      try {
        const value = storage.getItem(key);
        if (value !== null && value !== undefined) {
          return value;
        }
      } catch {
        // Try the next storage; reads must never break capture.
      }
    }

    return null;
  }

  setItem(key: string, value: string): void {
    let wroteToAnyStorage = false;

    for (const storage of this.storages) {
      try {
        storage.setItem(key, value);
        wroteToAnyStorage = true;
      } catch {
        // Partial writes are fine; a full failure is reported by throwing so
        // callers fall back to their in-memory copy.
      }
    }

    if (!wroteToAnyStorage) {
      throw new Error("storage write blocked");
    }
  }

  removeItem(key: string): void {
    for (const storage of this.storages) {
      try {
        storage.removeItem(key);
      } catch {
        // Ignore individual removal failures.
      }
    }
  }
}

const getStorage = (): StorageLike | null => {
  if (typeof window === "undefined") return null;

  const candidates: StorageLike[] = [];

  // Accessing the storage properties themselves can throw in restrictive
  // privacy modes, so each candidate is probed independently.
  try {
    if (window.localStorage) {
      candidates.push(window.localStorage);
    }
  } catch {
    // localStorage unavailable.
  }

  try {
    if (window.sessionStorage) {
      candidates.push(window.sessionStorage);
    }
  } catch {
    // sessionStorage unavailable.
  }

  if (candidates.length === 0) return null;
  if (candidates.length === 1) return candidates[0];

  return new CompositeStorage(candidates);
};

export const createCompositeStorageForTesting = (
  storages: StorageLike[],
): StorageLike => new CompositeStorage(storages);

const safeStorageGetItem = (
  storage: StorageLike | null,
  key: string,
): string | null => {
  if (!storage) return null;

  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
};

const getNurturingTokenFromUrl = (
  url: string,
): { hasParameter: boolean; token: string | null } | null => {
  try {
    const searchParams = new URL(url).searchParams;
    const hasParameter = searchParams.has(NURTURING_ATTRIBUTION_PARAM);
    const rawToken = searchParams.get(NURTURING_ATTRIBUTION_PARAM);

    return {
      hasParameter,
      token:
        rawToken && NURTURING_TOKEN_PATTERN.test(rawToken) ? rawToken : null,
    };
  } catch {
    return null;
  }
};

const getValidStoredNurturingAttribution = (
  value: string | null,
  now: number,
): StoredNurturingAttribution | null => {
  const stored = safeJsonParse<StoredNurturingAttribution>(value);
  if (
    !stored ||
    typeof stored.token !== "string" ||
    !NURTURING_TOKEN_PATTERN.test(stored.token) ||
    typeof stored.expires_at !== "number" ||
    stored.expires_at <= now
  ) {
    return null;
  }

  return stored;
};

export const clearPersistedNurturingAttribution = (
  storage: StorageLike | null = getStorage(),
): void => {
  inMemoryNurturingAttribution = null;
  removeStorageItem(storage, NURTURING_ATTRIBUTION_STORAGE_KEY);
};

export const getPersistedNurturingAttribution = (
  now = Date.now(),
  storage: StorageLike | null = getStorage(),
): StoredNurturingAttribution | null => {
  const storedValue = safeStorageGetItem(
    storage,
    NURTURING_ATTRIBUTION_STORAGE_KEY,
  );
  const stored = getValidStoredNurturingAttribution(storedValue, now);

  if (stored) {
    inMemoryNurturingAttribution = stored;
    return stored;
  }

  if (storedValue !== null) {
    removeStorageItem(storage, NURTURING_ATTRIBUTION_STORAGE_KEY);
  }

  if (
    inMemoryNurturingAttribution &&
    inMemoryNurturingAttribution.expires_at > now
  ) {
    return inMemoryNurturingAttribution;
  }

  inMemoryNurturingAttribution = null;
  return null;
};

export const persistNurturingAttribution = ({
  url,
  now = Date.now(),
  storage = getStorage(),
}: {
  url: string;
  now?: number;
  storage?: StorageLike | null;
}): string | null => {
  const parsed = getNurturingTokenFromUrl(url);
  if (!parsed) {
    return getPersistedNurturingAttribution(now, storage)?.token ?? null;
  }

  if (!parsed.hasParameter) {
    return getPersistedNurturingAttribution(now, storage)?.token ?? null;
  }

  if (!parsed.token) {
    clearPersistedNurturingAttribution(storage);
    return null;
  }

  const attribution: StoredNurturingAttribution = {
    token: parsed.token,
    created_at: now,
    expires_at: now + NURTURING_ATTRIBUTION_TTL_MS,
  };
  inMemoryNurturingAttribution = attribution;

  try {
    storage?.setItem(
      NURTURING_ATTRIBUTION_STORAGE_KEY,
      JSON.stringify(attribution),
    );
  } catch {
    // The in-memory copy keeps the current checkout working when storage is blocked.
  }

  return parsed.token;
};

export const stripNurturingAttributionFromUrl = (url: string): string => {
  try {
    const parsedUrl = new URL(url);
    parsedUrl.searchParams.delete(NURTURING_ATTRIBUTION_PARAM);
    return parsedUrl.toString();
  } catch {
    return "";
  }
};

const removeStorageItem = (
  storage: StorageLike | null,
  key: string,
): void => {
  if (!storage) return;

  try {
    storage.removeItem(key);
  } catch {
    // Ignore storage failures so attribution cleanup never blocks user flow.
  }
};

const clearLegacyAttributionStorage = (
  storage: StorageLike | null,
): void => {
  if (!storage || hasClearedLegacyAttributionStorage) return;

  try {
    storage.removeItem(LEGACY_ATTRIBUTION_STORAGE_KEY);
    hasClearedLegacyAttributionStorage = true;
  } catch {
    // Ignore storage failures so attribution cleanup never blocks user flow.
  }
};

const getValidStoredAttribution = (
  value: string | null,
  now: number,
): StoredAttribution | null => {
  const stored = safeJsonParse<StoredAttribution>(value);
  if (!stored || typeof stored.expires_at !== "number") {
    return null;
  }

  if (stored.expires_at <= now) {
    return null;
  }

  return stored;
};

const persistStoredAttribution = (
  storage: StorageLike | null,
  attribution: StoredAttribution,
): void => {
  storage?.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(attribution));
  clearLegacyAttributionStorage(storage);
};

type PersistedAttributionLookup = {
  attribution: StoredAttribution | null;
  source: "none" | "v2" | "legacy";
};

const createSessionId = (now: number): string => {
  let random: string;

  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      random = crypto.randomUUID();
    } else if (
      typeof crypto !== "undefined" &&
      typeof crypto.getRandomValues === "function"
    ) {
      const values = crypto.getRandomValues(new Uint32Array(2));
      random = `${values[0].toString(36)}${values[1].toString(36)}`;
    } else {
      // Last-resort analytics correlation only; never treat as security entropy.
      random = Math.random().toString(36).substring(2, 12);
    }
  } catch {
    // Last-resort analytics correlation only; never treat as security entropy.
    random = Math.random().toString(36).substring(2, 12);
  }

  return `session_${now}_${random}`;
};

export const getOrCreatePersistedSession = (
  now = Date.now(),
  storage: StorageLike | null = getStorage(),
): StoredSession => {
  if (!storage) {
    if (inMemorySession?.expires_at && inMemorySession.expires_at > now) {
      return inMemorySession;
    }

    inMemorySession = {
      session_id: createSessionId(now),
      created_at: now,
      expires_at: now + SESSION_TTL_MS,
    };

    return inMemorySession;
  }

  const stored = safeJsonParse<StoredSession>(
    safeStorageGetItem(storage, SESSION_STORAGE_KEY),
  );

  if (
    stored?.session_id &&
    typeof stored.created_at === "number" &&
    typeof stored.expires_at === "number" &&
    stored.expires_at > now
  ) {
    // Fixed 24h app-session window; do not slide expiry on activity.
    if (inMemorySessionStorage === storage) {
      inMemorySession = null;
      inMemorySessionStorage = null;
    }
    return stored;
  }

  if (
    inMemorySessionStorage === storage &&
    inMemorySession?.expires_at &&
    inMemorySession.expires_at > now
  ) {
    const fallbackSession = inMemorySession;
    try {
      storage.setItem(SESSION_STORAGE_KEY, JSON.stringify(fallbackSession));
      inMemorySession = null;
      inMemorySessionStorage = null;
      return fallbackSession;
    } catch {
      return fallbackSession;
    }
  }

  const session: StoredSession = {
    session_id: createSessionId(now),
    created_at: now,
    expires_at: now + SESSION_TTL_MS,
  };

  try {
    storage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
  } catch {
    // Storage-denied browsers keep this id in memory for the current page.
    inMemorySession = session;
    inMemorySessionStorage = storage;
    return session;
  }

  return session;
};

export const getOrCreatePersistedSessionId = (
  now = Date.now(),
  storage: StorageLike | null = getStorage(),
): string => getOrCreatePersistedSession(now, storage).session_id;

export const extractCurrentAttributionFromUrl = (
  url: string,
): Partial<Record<AttributionParamName, string>> => {
  const values: Partial<Record<AttributionParamName, string>> = {};

  try {
    const searchParams = new URL(url).searchParams;
    [...UTM_PARAM_NAMES, ...GOOGLE_AD_PARAM_NAMES].forEach((paramName) => {
      const value = searchParams.get(paramName);
      if (value) {
        values[paramName] = value;
      }
    });
  } catch {
    return values;
  }

  return values;
};

export const stripGoogleAdParamsFromUrl = (url: string): string => {
  try {
    const parsedUrl = new URL(url);
    AD_CLICK_URL_PARAM_NAMES.forEach((paramName) => {
      parsedUrl.searchParams.delete(paramName);
    });
    parsedUrl.username = "";
    parsedUrl.password = "";
    parsedUrl.hash = "";

    return parsedUrl.toString();
  } catch {
    return "";
  }
};

/**
 * Consent-withdrawn variant: **no** attribution parameter — UTM or Google ad —
 * may survive anywhere a URL is still transmitted, because the raw
 * `landing_page`/`referrer_url` fields would otherwise keep carrying the click
 * IDs after the structured attribution fields were nulled.
 */
export const stripAllAttributionParamsFromUrl = (url: string): string => {
  try {
    const parsedUrl = new URL(url);
    [...UTM_PARAM_NAMES, ...AD_CLICK_URL_PARAM_NAMES].forEach((paramName) => {
      parsedUrl.searchParams.delete(paramName);
    });
    parsedUrl.searchParams.delete(NURTURING_ATTRIBUTION_PARAM);
    parsedUrl.username = "";
    parsedUrl.password = "";
    parsedUrl.hash = "";

    return parsedUrl.toString();
  } catch {
    return "";
  }
};

export const sanitizeUrlToAttributionParams = (
  url: string,
  allowGoogleAdParams: boolean,
): string => {
  try {
    const parsedUrl = new URL(url);
    const allowedParams = new Set<string>(
      allowGoogleAdParams
        ? [...UTM_PARAM_NAMES, ...AD_CLICK_URL_PARAM_NAMES]
        : UTM_PARAM_NAMES,
    );

    Array.from(parsedUrl.searchParams.keys()).forEach((paramName) => {
      if (!allowedParams.has(paramName)) {
        parsedUrl.searchParams.delete(paramName);
      }
    });
    parsedUrl.username = "";
    parsedUrl.password = "";
    parsedUrl.hash = "";

    return parsedUrl.toString();
  } catch {
    return "";
  }
};

/**
 * Legacy v1 records were written before URL sanitization existed and can
 * contain emails, tokens or fragments in their landing/referrer URLs.
 * Migration re-sanitizes them so the pre-hardening cohort cannot keep feeding
 * unsanitized URLs into tracking payloads. Ad parameters are kept: legacy
 * records were captured with them and they are the point of the migration.
 */
const sanitizeLegacyStoredAttribution = (
  stored: StoredAttribution,
): StoredAttribution => {
  const sanitize = (value: string | null | undefined): string | null => {
    if (!value) return value ?? null;
    return sanitizeUrlToAttributionParams(value, true) || null;
  };

  return {
    ...stored,
    first_landing_page: sanitize(stored.first_landing_page),
    first_referrer_url: sanitize(stored.first_referrer_url),
  };
};

const normalizeAttributionUrlForComparison = (url: string): string => {
  const sanitizedUrl = sanitizeUrlToAttributionParams(url, false);
  if (!sanitizedUrl) return "";

  try {
    const parsedUrl = new URL(sanitizedUrl);
    const sortedParams = Array.from(parsedUrl.searchParams.entries()).sort(
      ([leftKey, leftValue], [rightKey, rightValue]) =>
        leftKey === rightKey
          ? leftValue.localeCompare(rightValue)
          : leftKey.localeCompare(rightKey),
    );
    parsedUrl.search = "";
    sortedParams.forEach(([key, value]) => {
      parsedUrl.searchParams.append(key, value);
    });

    return parsedUrl.toString();
  } catch {
    return "";
  }
};

export const stripGoogleAdParamsFromAttribution = <
  T extends AttributionValues,
>(
  attribution: T,
): T => {
  const sanitized = { ...attribution };

  GOOGLE_AD_PARAM_NAMES.forEach((paramName) => {
    delete sanitized[paramName];
  });

  if (sanitized.first_landing_page) {
    sanitized.first_landing_page = sanitizeUrlToAttributionParams(
      sanitized.first_landing_page,
      false,
    );
  }

  if (sanitized.first_referrer_url) {
    sanitized.first_referrer_url = sanitizeUrlToAttributionParams(
      sanitized.first_referrer_url,
      false,
    );
  }

  return sanitized;
};

export const buildFirstTouchAttribution = ({
  url,
  referrerUrl,
  now = Date.now(),
  allowGoogleAdParams = true,
  // When enabled, ad parameters survive even when allowGoogleAdParams is
  // false. Non-allowlisted query data (emails, tokens, third-party ids) is
  // always stripped, in both modes.
  preserveRawUrls = false,
}: {
  url: string;
  referrerUrl?: string;
  now?: number;
  allowGoogleAdParams?: boolean;
  preserveRawUrls?: boolean;
}): StoredAttribution => {
  // Raw mode keeps ad parameters that non-raw mode may have stripped, but it
  // never stores arbitrary query parameters: emails, tokens and third-party
  // click ids must not end up in storage or in tracking payloads.
  const landingPage = preserveRawUrls
    ? sanitizeUrlToAttributionParams(url, true)
    : sanitizeUrlToAttributionParams(url, allowGoogleAdParams);
  const currentValues = extractCurrentAttributionFromUrl(landingPage);
  const firstTouch: StoredAttribution = {
    ...currentValues,
    first_landing_page: landingPage || null,
    first_referrer_url: referrerUrl
      ? preserveRawUrls
        ? sanitizeUrlToAttributionParams(referrerUrl, true)
        : sanitizeUrlToAttributionParams(referrerUrl, allowGoogleAdParams)
      : null,
    created_at: now,
    expires_at: now + ATTRIBUTION_TTL_MS,
  };

  UTM_PARAM_NAMES.forEach((utmParamName) => {
    const value = currentValues[utmParamName];
    if (value) {
      const firstTouchKey = `first_${utmParamName}` as const;
      firstTouch[firstTouchKey] = value;
    }
  });

  return firstTouch;
};

const lookupPersistedFirstTouchAttribution = (
  now = Date.now(),
  storage: StorageLike | null = getStorage(),
): PersistedAttributionLookup => {
  if (!storage) {
    return { attribution: null, source: "none" };
  }

  const persistedBody = safeStorageGetItem(storage, ATTRIBUTION_STORAGE_KEY);
  const stored = getValidStoredAttribution(persistedBody, now);
  if (stored) {
    clearLegacyAttributionStorage(storage);
    return { attribution: stored, source: "v2" };
  }

  if (persistedBody) {
    removeStorageItem(storage, ATTRIBUTION_STORAGE_KEY);
  }

  if (hasClearedLegacyAttributionStorage) {
    return { attribution: null, source: "none" };
  }

  const legacyBody = safeStorageGetItem(storage, LEGACY_ATTRIBUTION_STORAGE_KEY);
  const legacyStored = getValidStoredAttribution(legacyBody, now);
  if (!legacyStored) {
    if (legacyBody) {
      clearLegacyAttributionStorage(storage);
    }
    return { attribution: null, source: "none" };
  }

  const sanitizedLegacyStored =
    sanitizeLegacyStoredAttribution(legacyStored);
  try {
    persistStoredAttribution(storage, sanitizedLegacyStored);
  } catch {
    // Best effort migration: return the still-valid legacy payload even if the
    // storage write is blocked so callers don't lose the original attribution.
  }

  return { attribution: sanitizedLegacyStored, source: "legacy" };
};

export const getPersistedFirstTouchAttribution = (
  now = Date.now(),
  storage: StorageLike | null = getStorage(),
): StoredAttribution | null => {
  return lookupPersistedFirstTouchAttribution(now, storage).attribution;
};

export const clearPersistedFirstTouchAttribution = (
  storage: StorageLike | null = getStorage(),
): void => {
  inMemoryAttribution = null;
  clearLegacyAttributionStorage(storage);
  removeStorageItem(storage, ATTRIBUTION_STORAGE_KEY);
};

const getInMemoryFirstTouchAttribution = (
  now = Date.now(),
): StoredAttribution | null => {
  if (!inMemoryAttribution || inMemoryAttribution.expires_at <= now) {
    inMemoryAttribution = null;
    return null;
  }

  return inMemoryAttribution;
};

const hasStoredAttributionChanged = (
  current: StoredAttribution,
  next: StoredAttribution,
): boolean => {
  // Merged records preserve storage metadata; any changed key means the stored
  // attribution payload should be rewritten.
  const currentRecord = current as unknown as Record<string, unknown>;
  const nextRecord = next as unknown as Record<string, unknown>;
  const keys = new Set([
    ...Object.keys(currentRecord),
    ...Object.keys(nextRecord),
  ]);

  for (const key of Array.from(keys)) {
    if (currentRecord[key] !== nextRecord[key]) {
      return true;
    }
  }

  return false;
};

export const persistFirstTouchAttribution = ({
  url,
  referrerUrl,
  now = Date.now(),
  storage = getStorage(),
  allowGoogleAdParams = true,
  persistToStorage = true,
  // When enabled, ad parameters survive even when allowGoogleAdParams is
  // false. Non-allowlisted query data (emails, tokens, third-party ids) is
  // always stripped, in both modes.
  preserveRawUrls = false,
}: {
  url: string;
  referrerUrl?: string;
  now?: number;
  storage?: StorageLike | null;
  allowGoogleAdParams?: boolean;
  persistToStorage?: boolean;
  preserveRawUrls?: boolean;
}): StoredAttribution => {
  const shouldKeepAdParams = preserveRawUrls || allowGoogleAdParams;
  const canRecoverAdParamsFromUrl = allowGoogleAdParams && !preserveRawUrls;
  const memoryExisting = getInMemoryFirstTouchAttribution(now);
  const persistedLookup = persistToStorage
    ? lookupPersistedFirstTouchAttribution(now, storage)
    : { attribution: null, source: "none" as const };
  const persistedExisting = persistedLookup.attribution;
  // When v2 writes were previously blocked, keep using the fresher in-memory
  // merge instead of reverting to the older legacy snapshot on this page.
  const shouldPreferMemoryExisting =
    persistToStorage &&
    persistedLookup.source === "legacy" &&
    memoryExisting !== null;
  const existing =
    shouldPreferMemoryExisting
      ? memoryExisting
      : persistToStorage
        ? persistedExisting ?? memoryExisting
        : memoryExisting;
  const shouldPersistMemoryExisting =
    persistToStorage &&
    existing === memoryExisting &&
    (!persistedExisting || shouldPreferMemoryExisting);
  const landingPage = preserveRawUrls
    ? sanitizeUrlToAttributionParams(url, true)
    : sanitizeUrlToAttributionParams(url, allowGoogleAdParams);
  const currentValues = extractCurrentAttributionFromUrl(landingPage);

  if (existing) {
    // Fixed first-touch window: merges keep the original created/expires times.
    // Raw mode keeps ad parameters but still drops non-allowlisted query data.
    const merged = shouldKeepAdParams
      ? { ...existing }
      : stripGoogleAdParamsFromAttribution(existing);

    [...UTM_PARAM_NAMES, ...GOOGLE_AD_PARAM_NAMES].forEach((paramName) => {
      if (currentValues[paramName]) {
        merged[paramName] = currentValues[paramName];
      }
    });

    if (!merged.first_landing_page && landingPage) {
      merged.first_landing_page = landingPage;
      if (referrerUrl && !merged.first_referrer_url) {
        const sanitizedReferrer = preserveRawUrls
          ? sanitizeUrlToAttributionParams(referrerUrl, true)
          : sanitizeUrlToAttributionParams(referrerUrl, allowGoogleAdParams);
        if (sanitizedReferrer) {
          merged.first_referrer_url = sanitizedReferrer;
        }
      }
      UTM_PARAM_NAMES.forEach((utmParamName) => {
        const value = currentValues[utmParamName];
        if (value) {
          const firstTouchKey = `first_${utmParamName}` as const;
          merged[firstTouchKey] = value;
        }
      });
    }

    if (
      canRecoverAdParamsFromUrl &&
      merged.first_landing_page &&
      normalizeAttributionUrlForComparison(merged.first_landing_page) ===
        normalizeAttributionUrlForComparison(landingPage)
    ) {
      // Best-effort recovery after pre-consent stripping: only restore ad IDs
      // while the browser is still on the same first-touch URL without ad params.
      merged.first_landing_page = landingPage;
    }

    if (canRecoverAdParamsFromUrl && referrerUrl && merged.first_referrer_url) {
      const sanitizedReferrer = sanitizeUrlToAttributionParams(
        referrerUrl,
        allowGoogleAdParams,
      );

      // Referrer ad IDs are only recovered while the current referrer still
      // matches the stored first referrer after ad params are stripped.
      if (
        sanitizedReferrer &&
        normalizeAttributionUrlForComparison(merged.first_referrer_url) ===
        normalizeAttributionUrlForComparison(sanitizedReferrer)
      ) {
        merged.first_referrer_url = sanitizedReferrer;
      }
    }

    if (hasStoredAttributionChanged(existing, merged) || shouldPersistMemoryExisting) {
      if (persistToStorage) {
        try {
          persistStoredAttribution(storage, merged);
          inMemoryAttribution = null;
        } catch {
          inMemoryAttribution = merged;
          // Ignore storage failures so attribution never blocks user flow.
        }
      } else {
        inMemoryAttribution = merged;
      }
    }

    return merged;
  }

  const firstTouch = buildFirstTouchAttribution({
    url,
    referrerUrl,
    now,
    allowGoogleAdParams,
    preserveRawUrls,
  });

  if (persistToStorage) {
    try {
      persistStoredAttribution(storage, firstTouch);
      inMemoryAttribution = null;
    } catch {
      inMemoryAttribution = firstTouch;
      // Ignore storage failures so attribution never blocks user flow.
    }
  } else {
    inMemoryAttribution = firstTouch;
  }

  return firstTouch;
};

export const omitStorageMetadata = (
  attribution: StoredAttribution | null,
): AttributionValues => {
  if (!attribution) return {};

  const values = { ...attribution };
  delete (values as Partial<StoredAttribution>).created_at;
  delete (values as Partial<StoredAttribution>).expires_at;

  return values;
};
